import * as runtime from '../generated-api/runtime';
import { API_CONFIG } from '../app/api-config';
import {
  KlaraBrief,
  KlaraConstants,
  KlaraInputs,
  KlaraLogEntry,
  KlaraProject,
  KlaraSource,
  KlaraVariables,
} from '../model/klara';

/**
 * Client for the Klara AI endpoints.
 *
 * Extends the generated runtime so it inherits the same base path, xsrf
 * middleware, credentials and error handling as every other call. Hand-written
 * only because `yarn api-gen` currently fails on 32 pre-existing spec errors in
 * unrelated endpoints — see src/model/klara.ts.
 */
class KlaraApi extends runtime.BaseAPI {
  /**
   * No company space id: the server resolves the current space from the host,
   * the same way every other space-scoped call in the app works.
   */
  private readonly base = '/company_spaces/generated_contents';

  async list(
    filters: { fulltext?: string; mine?: boolean } = {}
  ): Promise<KlaraProject[]> {
    const query: Record<string, string> = {};
    if (filters.fulltext) query.filter_fulltext = filters.fulltext;
    if (filters.mine) query.filter_mine = 'true';

    return this.json<KlaraProject[]>(this.base, 'GET', { query });
  }

  async get(id: string): Promise<KlaraProject> {
    return this.json<KlaraProject>(`${this.base}/${id}`, 'GET');
  }

  async create(
    inputs: KlaraInputs
  ): Promise<KlaraProject> {
    return this.json<KlaraProject>(this.base, 'POST', {
      body: { inputs },
    });
  }

  async update(
    id: string,
    inputs: KlaraInputs
  ): Promise<KlaraProject> {
    return this.json<KlaraProject>(
      `${this.base}/${id}`,
      'PATCH',
      { body: { inputs } }
    );
  }

  async remove(id: string): Promise<void> {
    await this.request({
      path: `${this.base}/${id}`,
      method: 'DELETE',
      headers: {},
    });
  }

  /**
   * Message 1. Answers 202: the dispatch runs in a job.
   *
   * autoEvaluate means the user filled the drafted sections themselves and
   * asked to continue, so message 2 follows brief_drafted without a second
   * click. Message 1 is never skippable — it is what creates the job at the
   * partner, registers the source files and carries the config.
   */
  async requestBrief(
    id: string,
    options: { autoEvaluate?: boolean } = {}
  ): Promise<KlaraProject> {
    return this.json<KlaraProject>(
      `${this.base}/${id}/request_brief`,
      'POST',
      { body: { auto_evaluate: options.autoEvaluate ?? false } }
    );
  }

  /**
   * Message 2. The finals are the user's edited zadání, sent whole.
   *
   * Answers 202: the finals are stored in the request, the send runs in a job.
   * So the status in this response is still the one from before the click —
   * KlaraPage covers that with its hand-off window.
   */
  async requestEvaluation(
    id: string,
    brief: { constants: KlaraConstants; variables: KlaraVariables }
  ): Promise<KlaraProject> {
    return this.json<KlaraProject>(
      `${this.base}/${id}/request_evaluation`,
      'POST',
      { body: brief }
    );
  }

  /**
   * Message 3. The finals are omitted unless the user actually revised the
   * zadání here — their absence tells the partner that the version from
   * message 2 stands.
   *
   * Answers 202, like the other two: the send runs in a job.
   */
  async requestGeneration(
    id: string,
    options: { research: boolean; brief?: KlaraBrief }
  ): Promise<KlaraProject> {
    const body: Record<string, unknown> = { research: options.research };
    if (options.brief) {
      body.constants = options.brief.constants ?? {};
      body.variables = options.brief.variables ?? {};
    }

    return this.json<KlaraProject>(
      `${this.base}/${id}/request_generation`,
      'POST',
      { body }
    );
  }

  /**
   * "Sestavit kurz". Answers 202: the build runs in a job, so the response's
   * transfer_status is already 'running' (the controller flips it before
   * enqueuing) — the caller polls the existing GET for completion.
   */
  async transfer(id: string): Promise<KlaraProject> {
    return this.json<KlaraProject>(`${this.base}/${id}/transfer`, 'POST');
  }

  async events(id: string): Promise<KlaraLogEntry[]> {
    return this.json<KlaraLogEntry[]>(
      `${this.base}/${id}/events`,
      'GET'
    );
  }

  /** The file itself is uploaded straight to the bucket first, then attached. */
  async attachSource(
    id: string,
    uploadedFileId: string
  ): Promise<KlaraSource> {
    return this.json<KlaraSource>(
      `${this.base}/${id}/sources`,
      'POST',
      { body: { uploaded_file_id: uploadedFileId } }
    );
  }

  async detachSource(
    id: string,
    sourceId: number
  ): Promise<void> {
    await this.request({
      path: `${this.base}/${id}/sources/${sourceId}`,
      method: 'DELETE',
      headers: {},
    });
  }

  /**
   * Every artifact in one streamed zip, or one type of it — the gallery, whose
   * images the Figma frame offers as their own archive.
   */
  archiveUrl(id: string, documentType?: string): string {
    const base = `${this.configuration.basePath}${this.base}/${id}/archive`;
    return documentType ? `${base}?document_type=${documentType}` : base;
  }

  /** A redirect to a presigned url, so the browser follows it directly. */
  downloadUrl(id: string, documentId: number): string {
    return `${this.configuration.basePath}${this.base}` +
      `/${id}/documents/${documentId}`;
  }

  private async json<T>(
    path: string,
    method: runtime.HTTPMethod,
    options: { body?: unknown; query?: Record<string, string> } = {}
  ): Promise<T> {
    const response = await this.request({
      path,
      method,
      headers: options.body
        ? { 'Content-Type': 'application/json' }
        : ({} as runtime.HTTPHeaders),
      query: options.query,
      body: options.body as any,
    });

    return new runtime.JSONApiResponse<T>(response).value();
  }
}

export const klaraApi = new KlaraApi(API_CONFIG);
